import { Check } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { formatBillingPrice, formatJudgementCredits } from "@/lib/billing-format"
import { cn } from "@/lib/utils"

export function PlanSelectionCard({
  checkoutAction,
  checkoutPending,
  plan,
  trial,
}) {
  const formattedPrice = formatBillingPrice(plan)

  return (
    <Card
      role="group"
      aria-label={`${plan.name} subscription plan`}
      className={cn("h-full", plan.popular && "border-primary/50 shadow-md")}
    >
      <CardHeader className="space-y-4">
        <div className="flex min-h-6 items-center justify-between gap-3">
          <CardTitle>{plan.name}</CardTitle>
          {plan.popular ? <Badge>Most popular</Badge> : null}
        </div>
        <p className="text-3xl font-semibold">
          {formattedPrice}
          <span className="text-base font-normal text-muted-foreground">/mo</span>
        </p>
        <CardDescription>
          {trial?.eligible
            ? `${trial.days} days free, then ${formattedPrice}/month`
            : "Monthly Sentinel access"}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-5">
        <p className="min-h-18 text-sm leading-6 text-muted-foreground">{plan.pitch}</p>

        <Separator />

        <dl className="space-y-4 text-sm">
          <div className="flex items-center justify-between gap-4">
            <dt className="text-muted-foreground">Judgement Credits</dt>
            <dd className="font-medium">
              {formatJudgementCredits(plan.judgement_credits)}<span className="font-normal text-muted-foreground">/mo</span>
            </dd>
          </div>
          <Separator />
          <div className="flex items-center justify-between gap-4">
            <dt className="text-muted-foreground">Support</dt>
            <dd className="font-medium">{plan.support}</dd>
          </div>
        </dl>

        <Separator />

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{plan.benefits_label}</p>
          <ul className="space-y-3 text-sm leading-6">
            {plan.benefits.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2">
                <Check aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-primary" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>

      <CardFooter className="mt-auto flex-col items-stretch gap-3">
        <form action={checkoutAction}>
          <input type="hidden" name="tier" value={plan.key} />
          <Button type="submit" className="w-full" disabled={checkoutPending}>
            {trial?.eligible ? "Start free trial" : `Choose ${plan.name}`}
          </Button>
        </form>
      </CardFooter>
    </Card>
  )
}
